import { useEffect, useContext, useState } from "react";
import { Box, Dialog, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"

import ArrowDropUpIcon 		from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon 	from '@mui/icons-material/ArrowDropDown';
import CloseIcon 			from '@mui/icons-material/Close';

import CompanyContext 		from "../context";
import { formatCurrency } 	from "../../../common/Helper";

const DialogQuarterResults = (props) => {

	const {company, currency, exchangeRate}		= useContext(CompanyContext)

	const open 									= props.open
	const setOpen								= props.setOpen
	const showInDollar							= props.showInDollar
	const setShowInDollar						= props.setShowInDollar
	const selectedYear 							= props.selectedYear

	const [firstYearData, setFirstYearData]		= useState()
	const [secondYearData, setSecondYearData]	= useState()
	
	useEffect(() => {
		if (selectedYear) {
			const financialQuarterReports		= company.financialQuarterReports.sort((a,b) => new Date(a.date) - new Date(b.date))

			// Busca os dados de ano atual e do anterior
			let firstYearQuarterData			= financialQuarterReports.filter(fqr => fqr.year ? fqr.year === selectedYear : new Date(fqr.date).getFullYear() === selectedYear)
			let secondYearQuarterData			= financialQuarterReports.filter(fqr => fqr.year ? fqr.year === selectedYear - 1 : new Date(fqr.date).getFullYear() === selectedYear - 1)

			// Se existem dados trimestrais
			if (secondYearQuarterData.length > 0) {
				// Caso o primeiro ano não esteja completo, insere os dados do ano anteior
				for(let i=firstYearQuarterData.length;i<4;i++) {
					if (secondYearQuarterData[i]) firstYearQuarterData.push({...secondYearQuarterData[i], isPreviousYearData: true})
				}

				for(let i=1;i<=4;i++) {
					if (!firstYearQuarterData.some(f => f.period == 'Q'+ i)) firstYearQuarterData.push({period : 'Q'+ i})
					if (!secondYearQuarterData.some(f => f.period == 'Q'+ i)) secondYearQuarterData.push({period : 'Q'+ i})
				}
				
				setFirstYearData({year : selectedYear, data : firstYearQuarterData.sort((a,b) => a.period.localeCompare(b.period))})
				setSecondYearData({year : selectedYear - 1, data : secondYearQuarterData.sort((a,b) => a.period.localeCompare(b.period))})
			}			
		}
	}, [selectedYear, company])

	function handleClose() {
		setOpen(false)
	}

	return (
		<Dialog onClose={handleClose} open={open} maxWidth={"xl"} fullWidth={true}>
			<DialogTitle variant={'h6'} display={'flex'} alignItems={'center'}>
				<Box flex={1}>Resultados Trimestrais</Box>
				<CloseIcon onClick={() => setOpen(false)} sx={{cursor: 'pointer'}}/>
			</DialogTitle>

			{firstYearData && secondYearData ? (
				<Stack direction="row" alignItems="top" gap={2} padding={2} >
					<Stack direction="column" width={'100%'}>
						<TableContainer component={Paper}>
							<Table>
								<FinancialTableHeader year={firstYearData.year}/>				
								<TableBody>
									<FinancialRow data={firstYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} compareData={secondYearData.data} label="Receita" format="$,0" item="revenue"/>
									<FinancialRow data={firstYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} compareData={secondYearData.data} label="EBITDA" format="$,0" item="ebitda"/>
									<FinancialRow data={firstYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} compareData={secondYearData.data} label="Lucro Líquido" format="$,0" item="netIncome"/>
								</TableBody>
							</Table>
						</TableContainer>
						<Typography variant="caption" textAlign={'right'} flex={1}>* Dados do trimestre do ano anterior</Typography>
					</Stack>
					<Stack direction="column" width={'100%'}>
						<TableContainer component={Paper}>
							<Table>
								<FinancialTableHeader year={secondYearData.year}/>			
								<TableBody>
									<FinancialRow data={secondYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Receita" format="$,0" item="revenue"/>
									<FinancialRow data={secondYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} label="EBITDA" format="$,0" item="ebitda"/>
									<FinancialRow data={secondYearData.data} showInDollar={showInDollar} exchangeRate={exchangeRate} label="Lucro Líquido" format="$,0" item="netIncome"/>
								</TableBody>
							</Table>
						</TableContainer>	
						<Box display={'flex'} marginBottom={1} paddingRight={2} justifyContent={'right'}>
							<Typography variant="caption">
								** Valores em milhões de {showInDollar ? 'USD' : currency}. 
							</Typography>
							{ currency !== 'USD' ? (
								<Typography variant="caption" paddingLeft={0.5} sx={{ cursor : 'pointer'}} onClick={() => setShowInDollar(!showInDollar)}>
									Mostrar em <u>{ showInDollar ? currency : 'USD' }</u>
								</Typography>
							) : null }
						</Box>	
					</Stack>
				</Stack>
			) : (
				<Typography padding={2} align="center"> Não há dados trimestrais </Typography>
			)}
		</Dialog>
	)
}

const FinancialTableHeader = (props) => {
	return (
		<TableHead>
		<TableRow>
			<TableCell colSpan={6} align="center">{props.year}</TableCell>
		</TableRow>
		<TableRow>
			<TableCell>Trimestre</TableCell>
			<TableCell align="center">1T</TableCell>
			<TableCell align="center">2T</TableCell>
			<TableCell align="center">3T</TableCell>
			<TableCell align="center">4T</TableCell>
			<TableCell align="center">Total</TableCell>
		</TableRow>
	</TableHead>	
	)
}

const FinancialRow = (props) => {

	let smallerIsBetter		= false
	let showInDollar		= props.showInDollar
	let exchangeRate		= props.exchangeRate
	let currentCurrency		= props.data[0]?.currency

	let financialCells 		= []
	let acc					= 0 
	let acc2				= 0

	if (props.smallerIsBetter) smallerIsBetter = props.smallerIsBetter

	// Cria um novo array 
	// 1) Convertendo os valores para dolar, se especificado;
	// 2) Calculando as variações ano a ano
	for (let i=0;i<props.data.length;i++) {
		const v1 	= props.data[i][props.item] || 0
		const v2 	= props.compareData && props.compareData[i][props.item] ? props.compareData[i][props.item] : 0

		acc 		= acc + parseFloat(v1)
		acc2		= acc2 + parseFloat(v2)

		financialCells.push({
			[props.item] 		: v1,
			variation 			: v2 ? (v1 - v2)/Math.abs(v2) * 100 : 0,
			isPreviousYearData	: props.data[i].isPreviousYearData
		})
	}

	// Adiciona o TOTAL no fim do Array
	financialCells.push({
		[props.item]		: acc,
		variation			: acc2 ? (acc - acc2)/Math.abs(acc2) * 100 : 0
	})

	const formatValue = (pValue, pFormat) => {
		if (isNaN(pValue)) pValue = 0

		switch (pFormat) {
			case '$,0' : return formatCurrency(showInDollar ? pValue * exchangeRate : pValue, 'BIG', showInDollar ? 'USD' : currentCurrency)
			case '$,2' : return formatCurrency(showInDollar ? pValue * exchangeRate : pValue, 'SMALL', showInDollar ? 'USD' : currentCurrency)
			case '%,2' : return (pValue * 100).toFixed(2) +'%'
			case '#,2' : return pValue.toFixed(2)
		}
	}

	return (
		<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
			<TableCell component="th" scope="row">{props.label}</TableCell>
			{ financialCells && financialCells.map((fr, index) => (
				<TableCell align="center" height={20} key={props.label + index}>
					<Box justifyContent={'center'} alignItems={'center'} display={'flex'}>
						<Typography variant="body2">{formatValue(fr[props.item], props.format)}</Typography>
						{ fr.variation && fr.variation !== 0 ? (
							<Tooltip title={fr.variation.toFixed(2) +'%'}>
								{ (fr.variation >= 0 ? <ArrowDropUpIcon style={{ color: smallerIsBetter ? 'red' : 'green' }} /> : <ArrowDropDownIcon style={{ color: smallerIsBetter ? 'green' : 'red' }} />) }	
							</Tooltip>
						):( fr.isPreviousYearData ? "*" : null )}
					</Box>
				</TableCell>
			)) }
		</TableRow>
	)
}

export default DialogQuarterResults